import useApi from 'api';
import Locale from 'data/locale';
import React from 'react';

import Store from 'store';
import History from 'store/history';
import EditingContext from 'store/editingContext';
import Overlay from 'ui/utils/Overlay';
import { OverlayContext } from 'ui/utils/OverlayManager';
import Dialog from 'ui/dialog';
import { profileFix } from 'logic/profileReducers';
import { getProfilePath } from 'common';

const _L = Locale("ui-import.js");
const _Lt = Locale("ui-timeline.js");

export const ErrorPopup = ({ onClose, reason }) => (
  <Overlay modal onMouseDown={onClose}>
    <div className="d3p-ErrorBox">
      <h3>{_L`Failed to load profile`}</h3>
      <p>{reason}</p>
    </div>
  </Overlay>
);

const OverwriteDialog = ({ onResolve, onClose }) => (
  <Dialog title={_L`Load profile`} modal onClose={onClose}>
    <Dialog.Content>
      <p>{_L`Discard current profile?`}</p>
    </Dialog.Content>
    <Dialog.Footer>
      <button onClick={() => onResolve(true)}>{_L`Yes`}</button>
      <button onClick={onClose}>{_L`No`}</button>
    </Dialog.Footer>
  </Dialog>
);

const OverwriteFollowerDialog = ({ onResolve, onClose }) => (
  <Dialog title={_L`Load profile`} modal onClose={onClose}>
    <Dialog.Content>
      <p>{_L`Discard current follower?`}</p>
    </Dialog.Content>
    <Dialog.Footer>
      <button onClick={() => onResolve(true)}>{_L`Yes`}</button>
      <button onClick={onClose}>{_L`No`}</button>
    </Dialog.Footer>
  </Dialog>
);

export default function useLoadProfile() {
  const overlays = React.useContext(OverlayContext);
  const [, setSpinner] = EditingContext.useState("spinner");
  const [, setPaperdollMode] = EditingContext.useState("paperdollMode");
  const api = useApi();

  const mainStore = Store.useStore();
  const historyStore = History.useStore();
  const dispatch = Store.useDispatch();

  return React.useCallback(id => {
    setSpinner(spin => spin + 1);
    api.load(id).then(
      async result => {
        const state = mainStore.getState();
        const history = historyStore.getState();
        const url = new URL(getProfilePath(id), window.location);
        const anyUndo = Object.values(history).some(state => state.prev);
        const isFollower = result.follower && Object.values(result.followerItems).some(Boolean) && !Object.values(result.items).some(Boolean);
        if (result.profiles) {
          if (anyUndo) {
            if (!await overlays.async(<OverwriteDialog/>)) {
              return;
            }
          }
          result.profiles = result.profiles.map(profile => profileFix(profile));
          dispatch.setState(result);
          window.history?.replaceState({}, "", url.href);
        } else if (isFollower) {
          const profile = state.profiles[state.activeProfile];
          if (history[profile.id]?.prev && Object.keys(profile.followerItems).length) {
            if (!await overlays.async(<OverwriteFollowerDialog/>)) {
              return;
            }
          }
          dispatch.setProfile({
            ...profile,
            follower: result.follower,
            followerItems: result.followerItems,
            followerSkills: result.followerSkills,
          });
        } else if (!anyUndo) {
          const prevName = result.name;
          result.name = _Lt`Set ${1}`;
          result = {
            profiles: [profileFix(result)],
            activeProfile: 0,
            active: {},
          };
          if (prevName) {
            result.name = prevName;
          }
          dispatch.setState(result);
          window.history?.replaceState({}, "", url.href);
        } else {
          const profile = state.profiles[state.activeProfile];
          result = profileFix(result);
          if (history[profile.id]?.prev) {
            if (!result.name) {
              result.name = _Lt`Set ${state.profiles.length + 1}`;
            }
            dispatch.addProfile(result);
          } else {
            const prevName = result.name;
            if (!result.name) {
              result.name = profile.name;
            }
            dispatch.setProfile(result);
            if (prevName && state.profiles.length === 1) {
              dispatch.setName(prevName);
            }
            if (state.profiles.length === 1) {
              window.history?.replaceState({}, "", url.href);
            }
          }
        }
        if (isFollower) {
          setPaperdollMode("follower");
        } else {
          setPaperdollMode("character");
        }
      },
      error => {
        overlays.async(<ErrorPopup reason={error.message}/>);
      },
    ).finally(() => setSpinner(spin => spin - 1));
  }, [api, overlays, setSpinner, setPaperdollMode, mainStore, historyStore, dispatch]);
}
